/**
 * Session Result Map
 *
 * Displays a map with features color-coded by quiz results:
 * - Green: Correctly answered
 * - Orange/Red: Incorrectly answered (areas to review)
 * - Gray: Not asked in this session
 */

import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import { QuizConfig, QuizAttempt } from "@/db/types";
import { fetchGeoJSONFromUrl, getFeatureIdentifier } from "@/utils/geojsonLoader";
import { applyFilters } from "@/utils/filterUtils";
import { LatLngBounds, PathOptions } from "leaflet";
import type { FilterRule } from "@/stores/useStudioStore";
import "leaflet/dist/leaflet.css";

interface SessionResultMapProps {
  config: QuizConfig;
  attempts: QuizAttempt[];
  height?: string;
}

export function SessionResultMap({ config, attempts, height = "400px" }: SessionResultMapProps) {
  const [geojson, setGeojson] = useState<GeoJSON.FeatureCollection | null>(null);
  const [loading, setLoading] = useState(true);

  // Load GeoJSON data
  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      try {
        setLoading(true);

        if (config.storedCollection) {
          // Inline collection (uploaded file) - already filtered and saved
          const rawCollection = JSON.parse(config.storedCollection);
          if (!cancelled) {
            setGeojson(rawCollection);
          }
        } else if (config.datasetSource?.type === "dataset-url") {
          const rawCollection = await fetchGeoJSONFromUrl(config.datasetSource.path);

          // Apply filters like QuizPreviewMap does
          const filters: FilterRule[] = config.filters.map((f, index) => ({
            ...f,
            id: `filter-${index}`,
          }));

          const processedCollection = applyFilters(rawCollection, filters);

          if (!cancelled) {
            setGeojson(processedCollection);
          }
        }
      } catch (error) {
        console.error("Failed to load GeoJSON for session map:", error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadData();
    return () => {
      cancelled = true;
    };
  }, [config]);

  // Create a map of featureId -> result
  const resultMap = useMemo(() => {
    const map = new Map<string, "correct" | "incorrect">();
    attempts.forEach((attempt: any) => {
      // Handle both local IndexedDB format and Supabase format
      const featureId = attempt.featureId || attempt.feature_id;
      const isCorrect = attempt.wasCorrect ?? (attempt.final_result === 'correct');

      if (isCorrect) {
        map.set(String(featureId), "correct");
      } else {
        map.set(String(featureId), "incorrect");
      }
    });
    return map;
  }, [attempts]);

  // Calculate bounds
  const bounds = useMemo(() => {
    if (!geojson) return undefined;

    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLng = Infinity;
    let maxLng = -Infinity;

    geojson.features.forEach((feature) => {
      if (feature.geometry.type === "Polygon") {
        feature.geometry.coordinates[0]?.forEach((coord) => {
          if (coord && coord.length >= 2) {
            const [lng, lat] = coord;
            if (typeof lng === "number" && typeof lat === "number") {
              minLat = Math.min(minLat, lat);
              maxLat = Math.max(maxLat, lat);
              minLng = Math.min(minLng, lng);
              maxLng = Math.max(maxLng, lng);
            }
          }
        });
      } else if (feature.geometry.type === "MultiPolygon") {
        feature.geometry.coordinates.forEach((polygon) => {
          polygon[0]?.forEach((coord) => {
            if (coord && coord.length >= 2) {
              const [lng, lat] = coord;
              if (typeof lng === "number" && typeof lat === "number") {
                minLat = Math.min(minLat, lat);
                maxLat = Math.max(maxLat, lat);
                minLng = Math.min(minLng, lng);
                maxLng = Math.max(maxLng, lng);
              }
            }
          });
        });
      }
    });

    if (!isFinite(minLat) || !isFinite(maxLat) || !isFinite(minLng) || !isFinite(maxLng)) {
      return undefined;
    }

    return new LatLngBounds([minLat, minLng], [maxLat, maxLng]);
  }, [geojson]);

  // Style features based on result
  const styleFeature = (feature: GeoJSON.Feature, index: number): PathOptions => {
    // Use the same feature identification logic as the quiz engine
    const featureId = getFeatureIdentifier(feature, index);
    const result = resultMap.get(featureId);

    if (result === "correct") {
      return {
        fillColor: "#22c55e", // green-500
        fillOpacity: 0.6,
        color: "#16a34a", // green-600
        weight: 2,
      };
    } else if (result === "incorrect") {
      return {
        fillColor: "#f97316", // orange-500
        fillOpacity: 0.6,
        color: "#ea580c", // orange-600
        weight: 2,
      };
    } else {
      // Not asked
      return {
        fillColor: "#d1d5db", // gray-300
        fillOpacity: 0.3,
        color: "#9ca3af", // gray-400
        weight: 1,
      };
    }
  };

  // Feature tooltips
  const onEachFeature = (feature: GeoJSON.Feature, layer: any) => {
    // Use the same feature identification logic as the quiz engine
    const featureId = getFeatureIdentifier(feature, geojson?.features.indexOf(feature) ?? 0);
    const result = resultMap.get(featureId);

    // Handle both local and Supabase data formats
    const attempt = attempts.find((a: any) => {
      const aFeatureId = a.featureId || a.feature_id;
      return String(aFeatureId) === featureId;
    });

    if (result && attempt) {
      const label = (attempt as any).featureLabel || (attempt as any).feature_label || String(featureId);
      let tooltipContent = `<strong>${label}</strong><br/>`;

      if (result === "correct") {
        tooltipContent += `✅ Correct`;
      } else if (result === "incorrect") {
        tooltipContent += `❌ To Review`;
        const attemptsCount = (attempt as any).attemptsUsed || (attempt as any).attempts_used || 1;
        if (attemptsCount > 1) {
          tooltipContent += `<br/>${attemptsCount} attempts used`;
        }

        // Show what they clicked if available
        const clickedLabel = (attempt as any).clickedFeatureLabel || (attempt as any).clicked_feature_label;
        if (clickedLabel && clickedLabel !== label) {
          tooltipContent += `<br/>You clicked: ${clickedLabel}`;
        }
      }

      layer.bindTooltip(tooltipContent, {
        sticky: true,
      });
    }
  };

  if (loading) {
    return (
      <div
        className="flex items-center justify-center bg-muted rounded-lg"
        style={{ height }}
      >
        <p className="text-sm text-muted-foreground">Loading map...</p>
      </div>
    );
  }

  if (!geojson || !bounds) {
    return (
      <div
        className="flex items-center justify-center bg-muted rounded-lg"
        style={{ height }}
      >
        <p className="text-sm text-muted-foreground">Map unavailable</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg overflow-hidden border" style={{ height }}>
      <MapContainer
        bounds={bounds}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <GeoJSON
          key={JSON.stringify(resultMap)} // Re-render when results change
          data={geojson}
          style={(feature) => {
            if (!feature || !geojson) return {};
            const index = geojson.features.indexOf(feature as any);
            return styleFeature(feature, index);
          }}
          onEachFeature={onEachFeature}
        />
      </MapContainer>
    </div>
  );
}
